import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useResourceUtilizationChart } from "../ResourceUtilizationChart/hooks/useResourceUtilizationChart";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import { useAuth } from "../../../stores/AuthContext";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import ResourceUtilizationChartSettings from "../ResourceUtilizationChart/ResourceUtilizationChartSettings";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import { ZONE_COLORS } from "../AttributeSumLineChart/utils/thresholdUtils";
import RollingDayPresets from "../../../features/filter/TabFilterModal/RollingDayPresets";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";

// --- Helpers kept from original (used by handlers below) ---

const GRANULARITY_LABELS = { day: "Day", week: "Week", month: "Month", year: "Year" };

const UTILIZATION_VALUE_MAX_PX = 48; // was text-5xl
const UTILIZATION_VALUE_MIN_PX = 20;

// Extracted so useAutoFitFontSize is called from its own component (mounted only while chart
// data is present) rather than conditionally within the tile's own render — see Rules of Hooks.
// No compact fallback: this is a percentage, not a raw large count.
const UtilizationBigValue = ({ text, color }) => {
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: UTILIZATION_VALUE_MAX_PX,
    minPx: UTILIZATION_VALUE_MIN_PX,
  });

  return (
    <div ref={ref} className="max-w-full truncate font-bold tabular-nums" style={{ color, fontSize: `${fontSizePx}px` }}>
      {text}
    </div>
  );
};

/** Uncontrolled date input — avoids Chrome cursor-reset on every React re-render.
 *  Commits on pointer-triggered change or Enter/Blur. */
const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = React.useRef(value);
  const inputRef = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  const isPointerRef = React.useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);
  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val) || isNaN(new Date(val + "T00:00:00").getTime())) return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => { isPointerRef.current = true; };
    const onKeyDown = () => { isPointerRef.current = false; };
    const onNativeChange = () => { if (isPointerRef.current) { isPointerRef.current = false; commit(); } };
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("keydown", onKeyDown);
    el.addEventListener("change", onNativeChange);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("keydown", onKeyDown);
      el.removeEventListener("change", onNativeChange);
    };
  }, [commit]);
  return (
    <input ref={inputRef} type="date" defaultValue={value}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      onBlur={commit} disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
  );
});

function computeDisplayRange(dateStr, gran) {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };
  const fmt = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  if (gran === "day") return { startDate: fmt(d), endDate: fmt(d) };
  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmt(mon), endDate: fmt(sun) };
  }
  return { startDate: fmt(d), endDate: fmt(d) };
}

function defaultAnchor(gran) {
  const localDateStr = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const today = new Date();
  if (gran === "day") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return localDateStr(y);
  }
  return localDateStr(today);
}

// --- Executive-specific helpers ---

function formatPeriodLabel(gran, anchorDate) {
  if (!anchorDate) return "";
  const d = new Date(anchorDate + "T00:00:00");
  if (isNaN(d.getTime())) return anchorDate;
  if (gran === "day") {
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const fmt = (dt, full) =>
      dt.toLocaleDateString("de-DE", full
        ? { day: "2-digit", month: "2-digit", year: "numeric" }
        : { day: "2-digit", month: "2-digit" });
    return `${fmt(mon, false)} – ${fmt(sun, true)}`;
  }
  if (gran === "month") {
    return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  }
  if (gran === "year") {
    return String(d.getFullYear());
  }
  return anchorDate;
}

function getStatusColor(value, warn, critical) {
  if (value >= warn) return ZONE_COLORS.good;
  if (value >= critical) return ZONE_COLORS.warning;
  return ZONE_COLORS.bad;
}

function getDashboardTabs(userId) {
  try {
    const key = userId
      ? `cintelion.dashboardConfig.${userId}`
      : "cintelion.dashboardConfig";
    const c = JSON.parse(localStorage.getItem(key) || "{}");
    return Array.isArray(c.tabs) ? c.tabs : [];
  } catch {
    return [];
  }
}

// --- Component ---

function ExecutiveResourceUtilizationTile({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    chartData,
    datasetLastDate,
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setEnabledGranularities,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    fetchMetadata,
    fetchChartData,
  } = useResourceUtilizationChart(activeFile, initialSettings);

  // --- Executive-only state (not in original hook) ---
  const [warnThreshold, setWarnThreshold] = useState(initialSettings?.warnThreshold ?? 60);
  const [criticalThreshold, setCriticalThreshold] = useState(initialSettings?.criticalThreshold ?? 40);
  const [targetTab, setTargetTab] = useState(initialSettings?.targetTab ?? "");
  const [, setSearchParams] = useSearchParams();

  // Settings persistence — all original fields + 3 Executive-only fields
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, selectedObjectType, cycleStartActivity, cycleEndActivity, configuredResources,
      shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNonWorkingDays, includeNoDataResources, multiResourceMode, enabledGranularities,
      viewResources, granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd,
      // Executive-only:
      warnThreshold, criticalThreshold, targetTab,
    }));
  }, [title, selectedObjectType, cycleStartActivity, cycleEndActivity, // eslint-disable-line react-hooks/exhaustive-deps
      configuredResources, shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNonWorkingDays, includeNoDataResources, multiResourceMode, enabledGranularities,
      viewResources, granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd,
      warnThreshold, criticalThreshold, targetTab]);

  // Title sync (same as original, different default label)
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Auslastung (Executive)");
  }, [title]);

  const { filterState } = useGlobalFilters();
  const { user } = useAuth();
  const isFirstFilterRenderRef = useRef(true);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const viewResourcesRef = useRef(viewResources);
  const resourceSelectionDirtyRef = useRef(false);

  useEffect(() => { viewResourcesRef.current = viewResources; }, [viewResources]);

  const displayRange = useMemo(
    () => computeDisplayRange(selectedDate, granularity),
    [selectedDate, granularity],
  );

  // --- Settings modal handlers (identical to original + Executive setters) ---

  const handleOpenSettings = useCallback(() => { setIsSettingsModalOpen(true); }, []);

  const handleApplySettings = useCallback(async (values) => {
    // Identical to original: keep current granularity if still enabled, else fallback
    const normalizedGranularity = values.enabledGranularities.includes(granularity)
      ? granularity
      : values.enabledGranularities[0] || "day";

    const nextViewResources =
      values.configuredResources.length > 0
        ? (() => {
            const kept = viewResources.filter((r) => values.configuredResources.includes(r));
            return kept.length > 0 ? kept : values.configuredResources;
          })()
        : [];

    if (onTitleChange) onTitleChange(values.title);

    // All original setters — identical to original handleApplySettings
    setTitle(values.title);
    setSelectedObjectType(values.selectedObjectType);
    setConfiguredResources(values.configuredResources);
    setShiftStart(values.shiftStart);
    setShiftEnd(values.shiftEnd);
    setBreaks(values.breaks);
    setToleranceThreshold(values.toleranceThreshold);
    setIgnoreShortTransitions(values.ignoreShortTransitions);
    setIncludeNonWorkingDays(values.includeNonWorkingDays);
    setIncludeNoDataResources(values.includeNoDataResources);
    setMultiResourceMode(values.multiResourceMode);
    setEnabledGranularities(values.enabledGranularities);
    setViewResources(nextViewResources);
    setGranularity(normalizedGranularity);

    // Executive-only setters
    setWarnThreshold(values.warnThreshold ?? warnThreshold);
    setCriticalThreshold(values.criticalThreshold ?? criticalThreshold);
    setTargetTab(values.targetTab ?? targetTab);

    await fetchChartData({
      ...values,
      granularity: normalizedGranularity,
      viewResources: nextViewResources,
      selectedDate,
    });
  }, [
    fetchChartData, granularity, selectedDate, onTitleChange, viewResources,
    setBreaks, setConfiguredResources, setEnabledGranularities, setIgnoreShortTransitions,
    setIncludeNonWorkingDays, setIncludeNoDataResources, setMultiResourceMode,
    setSelectedObjectType, setShiftEnd, setShiftStart, setTitle, setToleranceThreshold,
    setGranularity, setViewResources,
    warnThreshold, criticalThreshold, targetTab,
  ]);

  // --- Re-fetch when global filters change (identical to original) ---

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (configuredResources.length > 0 && selectedObjectType && viewResources.length > 0) {
      fetchChartData();
    }
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Auto-load & refresh (identical to original) ---

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => { if (isMetadataLoaded) await fetchChartData(); });
    }
  }, [onRefresh, isMetadataLoaded, fetchChartData]);

  // --- Header actions (identical to original) ---

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // --- Granularity handlers (kept from original, used by auto-fetch effect) ---

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewResources = overrides.viewResources ?? viewResourcesRef.current;
      if (!effectiveViewResources || effectiveViewResources.length === 0) return false;
      fetchChartData({
        viewResources: effectiveViewResources,
        viewMode: "granularity",
        ...overrides,
      });
      return true;
    },
    [fetchChartData],
  );

  const handleGranularityChange = useCallback(
    (g) => {
      setGranularity(g);
      const baseAnchor = selectedDate || defaultAnchor(g);
      const normalizedAnchor =
        datasetLastDate && baseAnchor > datasetLastDate ? datasetLastDate : baseAnchor;
      setSelectedDate(normalizedAnchor);
      triggerAutoApply({ granularity: g, selectedDate: normalizedAnchor });
    },
    [setGranularity, setSelectedDate, selectedDate, datasetLastDate, triggerAutoApply],
  );

  const handleViewResourcesChange = useCallback(
    (nextResources) => {
      viewResourcesRef.current = nextResources;
      setViewResources(nextResources);
    },
    [setViewResources],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      triggerAutoApply({ viewMode: "granularity", selectedDate: nextDate });
    },
    [setSelectedDate, triggerAutoApply],
  );

  // --- Tab navigation (Executive-only) ---

  const handleTileClick = useCallback(
    (e) => {
      if (!targetTab) return;
      e.stopPropagation();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", targetTab);
        return next;
      });
    },
    [targetTab, setSearchParams],
  );

  // --- Derived state (identical to original) ---

  const items = chartData?.chart_items || [];
  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showConfigPrompt = isMetadataLoaded && !loading && !error && configuredResources.length === 0;
  const showContent = isMetadataLoaded && !error && configuredResources.length > 0;
  const showChart = showContent && items.length > 0;
  const showNoData = showContent && !loading && chartData !== null && items.length === 0;
  const showSelectionPrompt = showContent && !loading && chartData === null;

  // Auto-fetch when content becomes available (identical to original)
  useEffect(() => {
    if (!showContent || loading || error || chartData !== null) return;
    if (viewResources.length === 0) return;
    if (viewMode !== "granularity") return;
    triggerAutoApply();
  }, [showContent, loading, error, chartData, viewResources, viewMode, triggerAutoApply]);

  // --- Render ---

  return (
    <BaseTile
      title={title}
      loading={loading}
      minHeight="sm"
      className={`h-full${targetTab ? " cursor-pointer" : ""}`}
      onClick={targetTab ? handleTileClick : undefined}
    >
      <TileContent layout="flex" direction="column" gap="md" grow>

        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Daten laden"
            message="Bitte erst Metadaten laden."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Laden
              </button>
            }
          />
        )}

        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Ressourcen konfigurieren"
            message="Einstellungen öffnen und Ressourcen sowie Granularität auswählen."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Einstellungen
              </button>
            }
          />
        )}

        {showContent && (
          <>
            {/* Compact controls bar — Granularity buttons + Date picker */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 no-drag flex flex-col gap-1.5">
              <div className="flex items-center flex-wrap gap-1.5">
                {enabledGranularities.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => handleGranularityChange(g)}
                    className={`px-2 py-1 rounded-md border text-xs font-medium ${
                      granularity === g
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    {GRANULARITY_LABELS[g] || g}
                  </button>
                ))}
                <DateInput
                  value={selectedDate}
                  onChange={handleSelectedDateCommit}
                  disabled={loading}
                />
              </div>
              <RollingDayPresets onChange={handleSelectedDateCommit} />
            </div>

            {showChart && chartData && (
              <div className="flex flex-col items-center justify-center flex-1 gap-2 py-6">
                <UtilizationBigValue
                  text={`${Number(chartData.overall_utilization_percentage || 0).toFixed(1)}%`}
                  color={getStatusColor(chartData.overall_utilization_percentage, warnThreshold, criticalThreshold)}
                />
                <div className="text-sm text-gray-500">Gesamt-Auslastung</div>
                <div
                  className="w-4 h-4 rounded-full mt-1"
                  style={{ backgroundColor: getStatusColor(chartData.overall_utilization_percentage, warnThreshold, criticalThreshold) }}
                />
                <div className="text-xs text-gray-400 mt-1">
                  {GRANULARITY_LABELS[granularity] || granularity}
                  {selectedDate ? ` · ${formatPeriodLabel(granularity, selectedDate)}` : ""}
                </div>
              </div>
            )}

            {showNoData && (
              <TileStatusContent
                status="empty"
                title="Keine Daten"
                message="Keine Auslastungsdaten für die gewählte Konfiguration."
              />
            )}

            {showSelectionPrompt && (
              <TileStatusContent
                status="empty"
                title="Konfiguration ausstehend"
                message="Ressourcen und Granularität in den Einstellungen wählen, dann Anwenden."
              />
            )}
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Fehler beim Laden"
            message={typeof error === "string" ? error : "Unerwarteter Fehler."}
            action={
              <button
                onClick={() => { if (isMetadataLoaded) fetchChartData(); else fetchMetadata(); }}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Retry
              </button>
            }
          />
        )}

      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Auslastung (Executive) – Einstellungen"
        getInitialValues={() => ({
          title,
          selectedObjectType,
          configuredResources: [...configuredResources],
          shiftStart,
          shiftEnd,
          breaks: breaks.map((b) => ({ ...b })),
          toleranceThreshold,
          ignoreShortTransitions,
          includeNonWorkingDays,
          includeNoDataResources,
          multiResourceMode,
          enabledGranularities: [...enabledGranularities],
          // Executive-only fields
          warnThreshold,
          criticalThreshold,
          targetTab,
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) =>
          !(
            isMetadataLoaded &&
            values.selectedObjectType &&
            values.configuredResources?.length > 0 &&
            selectedDate &&
            values.enabledGranularities?.length > 0
          ) || loading
        }
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        {({ values, setField, update }) => (
          <>
            <ResourceUtilizationChartSettings
              title={values.title}
              objectTypes={objectTypes}
              selectedObjectType={values.selectedObjectType}
              availableResources={availableResources}
              configuredResources={values.configuredResources}
              shiftStart={values.shiftStart}
              shiftEnd={values.shiftEnd}
              breaks={values.breaks}
              toleranceThreshold={values.toleranceThreshold}
              ignoreShortTransitions={values.ignoreShortTransitions}
              includeNonWorkingDays={values.includeNonWorkingDays}
              includeNoDataResources={values.includeNoDataResources}
              multiResourceMode={values.multiResourceMode}
              enabledGranularities={values.enabledGranularities}
              onTitleChange={(v) => setField("title", v)}
              onObjectTypeChange={(v) => setField("selectedObjectType", v)}
              onConfiguredResourcesChange={(v) => setField("configuredResources", v)}
              onShiftStartChange={(v) => setField("shiftStart", v)}
              onShiftEndChange={(v) => setField("shiftEnd", v)}
              onAddBreak={() =>
                update((p) => ({
                  ...p,
                  breaks: [...p.breaks, { start: "12:00", end: "12:30" }],
                }))
              }
              onRemoveBreak={(idx) =>
                update((p) => ({
                  ...p,
                  breaks: p.breaks.filter((_, i) => i !== idx),
                }))
              }
              onUpdateBreak={(idx, field, val) =>
                update((p) => ({
                  ...p,
                  breaks: p.breaks.map((b, i) =>
                    i === idx ? { ...b, [field]: val } : b,
                  ),
                }))
              }
              onToleranceThresholdChange={(v) => setField("toleranceThreshold", v)}
              onIgnoreShortTransitionsChange={(v) => setField("ignoreShortTransitions", v)}
              onIncludeNonWorkingDaysChange={(v) => setField("includeNonWorkingDays", v)}
              onIncludeNoDataResourcesChange={(v) => setField("includeNoDataResources", v)}
              onMultiResourceModeChange={(v) => setField("multiResourceMode", v)}
              onEnabledGranularitiesChange={(v) => setField("enabledGranularities", v)}
              loading={loading}
            />

            {/* Executive-only: Ampel-Schwellenwerte + Ziel-Tab */}
            <div className="border-t pt-5 mt-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grün ab (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={values.warnThreshold}
                    onChange={(e) => setField("warnThreshold", Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gelb ab (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={values.criticalThreshold}
                    onChange={(e) => setField("criticalThreshold", Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ziel-Tab bei Klick
                </label>
                <select
                  value={values.targetTab}
                  onChange={(e) => setField("targetTab", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">— kein Tab —</option>
                  {getDashboardTabs(user?.id).map((tab) => (
                    <option key={tab.id} value={tab.id}>{tab.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </TileSettingsModal>
    </BaseTile>
  );
}

export default ExecutiveResourceUtilizationTile;
